/**
 * codePageGate — the code page as a small state machine (signup E3b-1 step 5;
 * E3b-2a step 3 adds two profile holders; SIGNUP_Build-spec_Profile §0, §3.1–3.3;
 * SIGNUP_Build-spec_PXP).
 *
 * A guest (anon) scans the code:
 *
 *   rest ──scanned──▶ found ──rise (420 ms)──▶ share ──a share──▶ waiting ──name──▶ people
 *                                               │                    │
 *                                               └─tap outside─▶ people └─X or 60 s─▶ rest
 *
 * Two profile holders (pxp) — by a scan or nearby (Mark, 2026-10-21: nothing is
 * recorded until both have tapped):
 *
 *   rest ──offer / nearby──▶ found ──rise──▶ share ──chosen──▶ waitingPeer ──formed──▶ forming ──landed──▶ people
 *                                             ▲  │                  │
 *                                   restore ──┘  └─tap outside─▶ away (the strip)
 *            over / gone / X / 60 s, from any of these ──▶ rest
 *
 * - **The scan raises the sheet**, never a timer or a tap on the code (§3.1).
 * - **Waiting (anon) is a gate on the guest's name**; if the name is already
 *   known when the share lands, waiting is skipped. The X and the 60 s expiry
 *   drop back to the bare code, the room kept — a late name moves nothing (§3.3).
 * - **A tap outside the anon share sheet** folds it to People (E3a's fold).
 * - **pxp: a tap on the sheet holds the choice** (`chosen`) — nothing is shared
 *   or recorded until the server says both tapped (`formed`); then the page
 *   does this phone's writes (`forming`) and lands (`landed`).
 * - **pxp: a tap outside puts the sheet away** to the strip (F1, Mark
 *   2026-10-22): the offer stays; a tap on the strip restores the sheet.
 * - **pxp ends quietly** (`over` — the other phone left, went silent, the 60 s
 *   wait, the server said busy; `gone` — the nearby person stopped looking
 *   before anyone's code was claimed): back to the bare code, nobody told.
 * - **A nearby match lands on the waiting stage it started from**: the offer
 *   that follows (the scan the match runs underneath) carries the room, and
 *   the page taps it with the choice it already holds.
 * - **The caret** shows on the bare code and on the strip, and leaves for
 *   People with the last person met this visit (`close {room}`).
 * - **Every open and close of the code screen starts it bare** (`reset`).
 *
 * Pure: the page owns the timers, the server calls and the writes; this owns
 * what happens.
 */

export const STAGE = {
  REST: 'rest',
  FOUND: 'found',
  SHARE: 'share',
  WAITING: 'waiting',
  AWAY: 'away',
  WAITING_PEER: 'waitingPeer',
  FORMING: 'forming',
  PEOPLE: 'people',
};

/** The waiting sheets' expiry: 60 s (Mark 2026-10-20, over Profile §3.3's 30 s — a real guest took 44 s; the X is the fast way out). */
export const WAITING_EXPIRE_MS = 60000;

export const initialGate = {
  stage: STAGE.REST,
  room: null,
  nameKnown: false,
  repeat: false,
  // pxp only
  kind: null, // 'anon' | 'pxp'
  via: null, // 'scan' | 'nearby'
  peer: null, // { profileUserId, name, photoUrl }
  existingRoom: null, // a repeat: the room you already share (what they receive)
  choice: null, // the held share choice, applied once formed
};

const UNTAPPED = [STAGE.FOUND, STAGE.SHARE, STAGE.AWAY];
const PXP_LIVE = [...UNTAPPED, STAGE.WAITING_PEER];

/**
 * @param {typeof initialGate} state
 * @param {{type: string}} event
 *   anon: scanned {room, nameKnown, repeat} · shared · name {room} · fold
 *   pxp: offer {room, peer, repeat, via, existingRoom} · nearby {peer, repeat, existingRoom} ·
 *        chosen {choice} · restore · formed · landed {room} · over · gone {peerId}
 *   both: rise · dismiss · expire · close {room} · reset
 * @returns {typeof initialGate} — stage `people` carries the room to land on
 *   (null = no one just met)
 */
export const codePageGate = (state, event) => {
  const pxp = state.kind === 'pxp';
  switch (event.type) {
    case 'scanned':
      // A scan only counts on the bare code; mid-sheet, the sheet is about someone
      if (state.stage !== STAGE.REST) return state;
      return {
        ...initialGate,
        stage: STAGE.FOUND,
        kind: 'anon',
        room: event.room,
        nameKnown: !!event.nameKnown,
        repeat: !!event.repeat,
      };
    case 'offer':
      // D5 (Mark, 2026-10-22): a scan beats a nearby sheet nobody has tapped —
      // the server already ended the request to this phone when the scan claimed
      if (
        state.stage === STAGE.REST ||
        (pxp && state.via === 'nearby' && !state.room && UNTAPPED.includes(state.stage))
      ) {
        return {
          ...initialGate,
          stage: STAGE.FOUND,
          kind: 'pxp',
          via: event.via || 'scan',
          room: event.room,
          peer: event.peer || null,
          repeat: !!event.repeat,
          existingRoom: event.existingRoom || null,
        };
      }
      // The match a nearby request was waiting on: the room arrives
      if (
        pxp &&
        state.stage === STAGE.WAITING_PEER &&
        state.via === 'nearby' &&
        !state.room &&
        state.peer?.profileUserId === event.peer?.profileUserId
      ) {
        return {
          ...state,
          room: event.room,
          repeat: !!event.repeat,
          existingRoom: event.existingRoom || state.existingRoom,
        };
      }
      return state;
    case 'nearby':
      if (state.stage !== STAGE.REST) return state;
      return {
        ...initialGate,
        stage: STAGE.FOUND,
        kind: 'pxp',
        via: 'nearby',
        peer: event.peer,
        repeat: !!event.repeat,
        existingRoom: event.existingRoom || null,
      };
    case 'rise':
      return state.stage === STAGE.FOUND ? { ...state, stage: STAGE.SHARE } : state;
    case 'shared':
      if (state.stage !== STAGE.SHARE || pxp) return state;
      return { ...state, stage: state.nameKnown ? STAGE.PEOPLE : STAGE.WAITING };
    case 'chosen':
      if (state.stage !== STAGE.SHARE || !pxp) return state;
      return { ...state, stage: STAGE.WAITING_PEER, choice: event.choice || null };
    case 'fold':
      if (state.stage !== STAGE.SHARE) return state;
      return { ...state, stage: pxp ? STAGE.AWAY : STAGE.PEOPLE };
    case 'restore':
      return state.stage === STAGE.AWAY ? { ...state, stage: STAGE.SHARE } : state;
    case 'formed':
      if (state.stage !== STAGE.WAITING_PEER) return state;
      return { ...state, stage: STAGE.FORMING, room: event.room || state.room };
    case 'landed':
      // The room this phone landed on (the one they share, on a repeat)
      return state.stage === STAGE.FORMING
        ? { ...state, stage: STAGE.PEOPLE, room: event.room || state.room }
        : state;
    case 'over':
      return pxp && PXP_LIVE.includes(state.stage) ? { ...initialGate } : state;
    case 'gone':
      // The nearby person stopped looking — only before anyone's code was claimed
      if (!pxp || state.via !== 'nearby' || state.room || !PXP_LIVE.includes(state.stage))
        return state;
      return state.peer?.profileUserId === event.peerId ? { ...initialGate } : state;
    case 'name':
      if (event.room !== state.room || pxp) return state;
      if (state.stage === STAGE.WAITING) return { ...state, nameKnown: true, stage: STAGE.PEOPLE };
      // Back on the bare code (dismissed or expired): a late name moves nothing
      return { ...state, nameKnown: true };
    case 'dismiss':
    case 'expire':
      return state.stage === STAGE.WAITING || state.stage === STAGE.WAITING_PEER
        ? { ...initialGate }
        : state;
    case 'close':
      // The caret: on the bare code, and on the strip (the page leaves the offer)
      if (state.stage !== STAGE.REST && state.stage !== STAGE.AWAY) return state;
      return { ...initialGate, stage: STAGE.PEOPLE, room: event.room ?? null };
    case 'reset':
      return state.stage === STAGE.REST && state.room === null ? state : { ...initialGate };
    default:
      return state;
  }
};

/** A pxp offer is live on the page (the page polls it while this holds). */
export const offerLive = (state) =>
  state.kind === 'pxp' && !!state.room && PXP_LIVE.includes(state.stage);

/**
 * The code tells the truth (Mark, 2026-10-22): a code that is taken — its
 * owner waiting on someone, or mid-exchange on a claimed room — can't be read.
 * A nearby sheet put away before anyone tapped is not taken: the strip exists
 * so the code can be held out (D4).
 */
export const codeTaken = (state) =>
  state.stage === STAGE.WAITING ||
  state.stage === STAGE.WAITING_PEER ||
  state.stage === STAGE.FORMING ||
  offerLive(state);
